import { defineComponent, ref, computed, onMounted } from 'vue'
import { RouterLink, useRouter, useRoute } from 'vue-router'
import { useSmartBack } from '@/composables/use-smart-back'
import {
  NavBar as VanNavBar,
  Icon as VanIcon,
  Sticky as VanSticky,
  Swipe as VanSwipe,
  SwipeItem as VanSwipeItem,
  Popup as VanPopup,
  Picker as VanPicker,
  Empty as VanEmpty,
} from 'vant'
import { useArticleStore } from '@/stores/article-store'
import { getApiV1ArticleTypeSearch, getApiV1BannersList } from '@/api/sdk.gen'
import { renderArticleList } from '@/components/article-list-view'
import type { Article, Banner } from '@/api/types.gen'
import { ExternalAnchor } from '@/components/external-anchor'

const PAGE_SIZE = 10

// 旧 UnoCSS 页面遗留导出占位（article-search.tsx 的 _Original 还在引用；实际未渲染）
export const ArticleCard: any = defineComponent({
  name: 'ArticleCard',
  props: { article: Object, onClick: Function },
  setup: () => () => null,
})

export default defineComponent({
  name: 'ArticleListPage',
  setup() {
    const router = useRouter()
  const goBack = useSmartBack()
    const route = useRoute()
    const articleStore = useArticleStore()

    const typeId = computed(() => Number(route.query.typeId) || 0)
    const lotteryType = computed(() => {
      const v = Number(route.query.lotteryType)
      return v === 1 || v === 2 ? v : undefined
    })
    // 对齐 49208 /corpusdetaillist：标题走 query.webtitle → route.meta.title，与具体分类名 typeName 无关
    const webtitle = computed(() =>
      String(route.query.webtitle ?? (route.meta?.title as string | undefined) ?? '资料大全详情'),
    )
    const year = ref(Number(route.query.year) || new Date().getFullYear())

    const articles = ref<Article[]>([])
    const page = ref(1)
    const total = ref(0)
    const loading = ref(false)
    const finished = ref(false)
    const refreshing = ref(false)
    const error = ref(false)
    const banners = ref<Banner[]>([])
    const swiperShow = ref(true)

    const showYear = ref(false)

    const titles = computed(() => `${year.value}年${webtitle.value}`)
    const yearColumns = computed(() =>
      articleStore.yearList.map((y) => ({ text: String(y), value: y })),
    )

    async function loadPage(reset = false) {
      loading.value = true
      error.value = false
      try {
        if (reset) {
          page.value = 1
          finished.value = false
        }
        const res = await getApiV1ArticleTypeSearch({
          query: {
            year: year.value,
            typeId: typeId.value,
            page: page.value,
            size: PAGE_SIZE,
            lotteryType: lotteryType.value,
          },
        })
        const data = res.data?.data
        const list = data?.list ?? []
        total.value = data?.total ?? 0
        articles.value = reset ? list : [...articles.value, ...list]
        if (list.length < PAGE_SIZE || articles.value.length >= total.value) {
          finished.value = true
        } else {
          page.value++
        }
      } catch {
        error.value = true
      } finally {
        loading.value = false
        refreshing.value = false
      }
    }

    async function fetchBanners() {
      const res = await getApiV1BannersList({ query: { page: 1, size: 999, status: 1, type: 6 } })
      banners.value = res.data?.data?.list ?? []
    }

    function onLoad() {
      loadPage(false)
    }

    function onRefresh() {
      loadPage(true)
    }

    function onConfirmYear({ selectedValues }: { selectedValues: number[] }) {
      showYear.value = false
      const y = selectedValues[0]
      if (y && y !== year.value) {
        year.value = y
        loadPage(true)
      }
    }

    onMounted(() => {
      articleStore.fetchYearListIfEmpty()
      loadPage(true)
      fetchBanners()
    })

    return () => (
      <div class="corpusdetaillist-msg">
        <VanSticky>
          <VanNavBar title={titles.value} onClickLeft={() => goBack()}>
            {{
              left: () => <VanIcon name="arrow-left" color="#333" size="16" />,
              right: () => (
                <div>
                  <div>
                    {/* 样式挂在 `.van-nav-bar__right > div > div > div` 上：div 保留，链接/按钮放里面 */}
                    <div>
                      <RouterLink class="btn-reset block" to={`/articles/search?typeId=${typeId.value}`}>
                        <img src="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABIAAAASCAMAAABhEH5lAAABLFBMVEX6/vv6/v37/v39/v7+/v7+//7////z/fj1/fn4/frt+/Pv+/Xw+/Xo+fHg+Ozh+Ozi+O3e9+rS9OPU9OTX9OXM89/N89/P8+DH8dvH8dzJ8dzD8Ni579K679O979S979W17M+27M+27NG47tKt68uu68uv68yr6sqm6Man6MaZ5r2H4rKI4rOK4rSA4K6B4K6B4K+C4K933ah43ql53qlz3KZw26Nm2Z5o2p5p2Z9i2Jpk2JtW1ZNY1pRH0YlJ0otK0otL0YxG0Ig/0IU5zoA6zoA6zoE8zoI8z4EzzXwxzHsjyXIkyXIkyXMdx28ex28fx3AYxmsaxmwcxm0UxWkVxWoQw2YRw2YSw2cTw2cTw2gKw2ILw2MMw2QOw2UHwmAIwmEJwmEKwmIHwWBS8aj1AAAAY3RSTlPR0dHR0dHR0tLS09PT1NXV1dbX19fY2NjZ2dna29vb29zc3Nzd3d3e39/h5OTk5eXl5efn5+jp6urq6+vt7fDw8PDx8vPz8/Pz9PX4+Pj5+fn6+vr7+/z8/Pz8/f39/f7+/v5xc4TYAAAAwklEQVQYGQXBhyICABQAwIccsvdeqey998relDJC/P8/uAskpo5fPvKX83VA0J4rbU/2p9ZuHoZAaH88aQYN6+UsRE3uoBpgId+GmHprBpA420UcbQJgOl9NPGcA0PjdS1QGAKA4QZSGARA/fcT5IkCyx+hnHTF7WwXJrs6nO/uHiKb7FYwV/4p7ifHfQYRUea7W1nLLTH1D4aIWQfr9NFMP0oUNBFp33iqFKxgsrRKgqjvVAUZelwQAIPU1HwAA2et/Uwgdd6UN1UUAAAAASUVORK5CYII=" alt="" />
                        <div>搜索</div>
                      </RouterLink>
                    </div>
                    <div>
                      <button type="button" class="btn-block" onClick={() => (showYear.value = true)}>
                        <VanIcon name="notes-o" color="#07c160" size="16" />
                        <span class="block">年份</span>
                      </button>
                    </div>
                  </div>
                </div>
              ),
            }}
          </VanNavBar>
        </VanSticky>

        {banners.value.length > 0 && swiperShow.value && (
          <div class="vant-swiper-wrap">
            <VanSwipe autoplay={3000} indicatorColor="white" autoHeight>
              {banners.value.map((b) => (
                <VanSwipeItem key={b.id}>
                  <ExternalAnchor url={b.url ?? ''}>
                    <img src={b.images ?? ''} alt={b.title ?? ''} />
                  </ExternalAnchor>
                </VanSwipeItem>
              ))}
            </VanSwipe>
            <VanIcon
              class="close"
              name="clear"
              size="16"
              color="#fff"
              onClick={() => (swiperShow.value = false)}
            />
          </div>
        )}

        {articles.value.length === 0 && !loading.value && <VanEmpty description="暂无数据" />}
        {renderArticleList({
          router,
          list: articles.value,
          refreshing,
          loading,
          error,
          finished: finished.value,
          onRefresh,
          onLoad,
        })}

        <VanPopup v-model:show={showYear.value} position="bottom">
          <VanPicker
            title="选择年份"
            columns={yearColumns.value}
            onConfirm={onConfirmYear}
            onCancel={() => (showYear.value = false)}
          />
        </VanPopup>
      </div>
    )
  },
})
